import { supabase } from '@/integrations/supabase/client';
import { buildPrompt, getSupportedAssets } from '@/prompts';

/**
 * Service pour gérer les prompts utilisateurs en base de données
 * Supporte les marchés individuels (gold, indices, crypto) et les sous-marchés (us30, nas100, etc.)
 * 
 * The default prompts now come from the modular prompt system in src/prompts/
 */
class PromptService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.lastCacheUpdate = new Map();
    
    // Liste des marchés valides (incluant toutes les cryptos)
    this.validMarkets = [
      'gold', 'indices', 'crypto',
      'us30', 'nas100', 'spp500', 'ger30',
      'btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 'dogeusd', 'avaxusd', 'linkusd', 'maticusd', 'dotusd'
    ];
    
    // Default prices for generating prompts (will be overridden by real prices)
    this.defaultPrices = {
      gold: 4185.00,
      us30: 45300,
      nas100: 21900,
      spp500: 6125,
      ger30: 21000,
      indices: 5000,
      btcusd: 105000,
      ethusd: 4000,
      solusd: 220,
      xrpusd: 2.50,
      adausd: 1.10,
      dogeusd: 0.35,
      avaxusd: 45,
      linkusd: 28,
      maticusd: 1.20,
      dotusd: 12,
      crypto: 100000
    };
  }

  /**
   * Get default prompts from the new modular prompt system
   * These are generated dynamically using the buildPrompt function
   */
  getDefaultPrompts() {
    const defaults = {};
    
    for (const market of this.validMarkets) {
      const price = this.defaultPrices[market] || 1000;
      defaults[market] = buildPrompt(market, { currentPrice: price });
    }
    
    return defaults;
  }

  /**
   * Get a single default prompt for a specific market
   */
  getDefaultPrompt(market, currentPrice = null) {
    const price = currentPrice || this.defaultPrices[market] || 1000;
    return buildPrompt(market, { currentPrice: price });
  }

  // ✅ VÉRIFIER SI LE CACHE EST VALIDE
  isCacheValid(market) {
    const lastUpdate = this.lastCacheUpdate.get(market);
    if (!lastUpdate) return false;
    return (Date.now() - lastUpdate) < this.cacheExpiry;
  }

  // ✅ RÉCUPÉRER UN PROMPT (avec cache)
  async getPrompt(market, currentPrice = null) {
    try {
      // Vérifier le cache d'abord
      if (this.cache.has(market) && this.isCacheValid(market)) {
        console.log(`📋 Using cached prompt for ${market}`);
        return this.cache.get(market);
      }

      console.log(`🔍 Fetching ${market} prompt from database...`);

      const { data, error } = await supabase.rpc('get_user_prompts');

      if (error) {
        console.error(`❌ Error fetching prompts:`, error);
        return this.getDefaultPrompt(market, currentPrice);
      }

      if (data && data.success && data.prompts) {
        const promptValue = data.prompts[market];
        let promptContent = typeof promptValue === 'string' 
          ? promptValue 
          : (promptValue?.content || promptValue);
        
        if (promptContent && promptContent.trim().length > 0) {
          // Auto-migrate ATLAS branding
          if (promptContent.includes('ATLAS') || promptContent.includes('Atlas')) {
            promptContent = promptContent
              .replace(/You are ATLAS/gi, 'You are Snap Chart AI')
              .replace(/as ATLAS/gi, 'as Snap Chart AI')
              .replace(/ATLAS,/gi, 'Snap Chart AI,');
          }
          
          this.cache.set(market, promptContent);
          this.lastCacheUpdate.set(market, Date.now());
          
          console.log(`✅ ${market} prompt loaded from database (${promptContent.length} chars)`);
          return promptContent;
        }
      }

      console.log(`📋 No ${market} prompt in database, using modular default`);
      return this.getDefaultPrompt(market, currentPrice);

    } catch (error) {
      console.error(`💥 Error in getPrompt for ${market}:`, error);
      return this.getDefaultPrompt(market, currentPrice);
    }
  }

  // ✅ SAUVEGARDER UN PROMPT
  async savePrompt(market, content) {
    try {
      console.log(`💾 Saving ${market} prompt to database...`);

      if (!content || content.trim().length === 0) {
        throw new Error('Prompt content cannot be empty');
      }

      if (!this.validMarkets.includes(market)) {
        throw new Error(`Invalid market. Must be one of: ${this.validMarkets.join(', ')}`);
      }

      const { data, error } = await supabase.rpc('save_user_prompt', {
        p_market: market,
        p_prompt_content: content.trim()
      });

      if (error) {
        console.error(`❌ Error saving prompt:`, error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (data && !data.success) {
        console.error(`❌ Save failed:`, data.error);
        throw new Error(data.error);
      }

      this.cache.set(market, content.trim());
      this.lastCacheUpdate.set(market, Date.now());

      console.log(`✅ ${market} prompt saved successfully (${content.length} chars)`);
      
      return {
        success: true,
        message: `${market} prompt saved successfully`,
        length: content.length
      };

    } catch (error) {
      console.error(`💥 Error saving ${market} prompt:`, error);
      throw error;
    }
  }

  // ✅ RÉCUPÉRER TOUS LES PROMPTS
  async getAllPrompts() {
    try {
      console.log('🔍 Fetching all prompts from database...');

      const { data, error } = await supabase.rpc('get_user_prompts');

      if (error) {
        console.error('❌ Error fetching all prompts:', error);
        return this.getDefaultPrompts();
      }

      if (data && data.success && data.prompts) {
        const prompts = {};
        const defaultPrompts = this.getDefaultPrompts();

        // Mélanger prompts DB + prompts par défaut
        for (const market of this.validMarkets) {
          if (data.prompts[market]) {
            const promptValue = data.prompts[market];
            prompts[market] = typeof promptValue === 'string' 
              ? promptValue 
              : (promptValue?.content || promptValue);
            
            this.cache.set(market, prompts[market]);
            this.lastCacheUpdate.set(market, Date.now());
          } else {
            prompts[market] = defaultPrompts[market];
          }
        }

        console.log('✅ All prompts loaded:', Object.keys(prompts));
        return prompts;
      }

      console.log('📋 No prompts in database, using modular defaults');
      return this.getDefaultPrompts();

    } catch (error) {
      console.error('💥 Error fetching all prompts:', error);
      return this.getDefaultPrompts();
    }
  }

  // ✅ SUPPRIMER UN PROMPT (revenir au défaut)
  async deletePrompt(market) {
    try {
      console.log(`🗑️ Deleting ${market} prompt from database...`);

      const { data, error } = await supabase.rpc('delete_user_prompt', {
        p_market: market
      });

      if (error) {
        console.error(`❌ Error deleting prompt:`, error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (data && !data.success) {
        console.error(`❌ Delete failed:`, data.error);
        throw new Error(data.error);
      }

      this.cache.delete(market);
      this.lastCacheUpdate.delete(market);

      console.log(`✅ ${market} prompt deleted successfully`);
      
      return {
        success: true,
        message: `${market} prompt deleted successfully`
      };

    } catch (error) {
      console.error(`💥 Error deleting ${market} prompt:`, error);
      throw error;
    }
  }

  /**
   * Reset a prompt to the modular default
   * @param {string} market - Market identifier
   * @param {number} currentPrice - Optional current price for dynamic generation
   */
  async resetToDefault(market, currentPrice = null) {
    try {
      // First delete from database
      await this.deletePrompt(market);
      
      // Return the fresh default prompt
      return this.getDefaultPrompt(market, currentPrice);
    } catch (error) {
      console.error(`💥 Error resetting ${market} prompt:`, error);
      throw error;
    }
  }

  // ✅ VIDER LE CACHE
  clearCache() {
    this.cache.clear();
    this.lastCacheUpdate.clear();
    console.log('🧹 Prompt cache cleared');
  }

  // ✅ STATUS DU SERVICE
  getStatus() {
    return {
      cacheSize: this.cache.size,
      cachedMarkets: Array.from(this.cache.keys()),
      lastUpdates: Object.fromEntries(this.lastCacheUpdate.entries()),
      validMarkets: this.validMarkets,
      supportedAssets: getSupportedAssets()
    };
  }
}

const promptService = new PromptService();
export default promptService;
